import { useState } from 'react';
import { Copy, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';

export interface PasskeyRegistrationModalProps {
  open: boolean;
  link: { url: string; qrSvg: string; expiresAt: string } | null;
  onClose: () => void;
  title?: string;
  description?: string;
}

/**
 * Displayed after a super-admin issues a passkey registration link or an
 * ExpoApp magic-link URL. Shows the URL and an inline SVG QR code.
 */
export function PasskeyRegistrationModal({
  open,
  link,
  onClose,
  title = 'Passkey registration',
  description,
}: PasskeyRegistrationModalProps) {
  const [copied, setCopied] = useState(false);

  if (!link) {
    return (
      <Modal
        open={open}
        onClose={onClose}
        title={title}
        description="No link available."
      >
        <p className="text-sm text-gray-500">No link has been generated yet.</p>
      </Modal>
    );
  }

  const expires = new Date(link.expiresAt);
  const subtitle =
    description ?? `Share this link with the user. Valid until ${expires.toLocaleTimeString()}.`;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      description={subtitle}
      size="md"
      footer={
        <Button onClick={onClose} variant="secondary">
          Done
        </Button>
      }
    >
      <div className="space-y-4">
        {/* The server-generated SVG is shown as an image, never injected into the DOM. */}
        <img
          className="mx-auto w-40"
          alt="Passkey registration QR code"
          src={`data:image/svg+xml;utf8,${encodeURIComponent(link.qrSvg)}`}
        />
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs dark:border-gray-800 dark:bg-gray-900/50">
          <div className="flex items-start gap-2">
            <code className="flex-1 font-mono break-all text-gray-800 dark:text-gray-200">
              {link.url}
            </code>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              leftIcon={
                copied ? (
                  <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                ) : (
                  <Copy className="h-4 w-4" />
                )
              }
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(link.url);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                } catch {
                  // ignore — clipboard is optional
                }
              }}
            >
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
